import { conversationsApi, ConversationsApiError, type Conversation, type ConversationsListResponse } from '@/services/conversationsApi'
import { CONVERSATIONS_API_URL, getConversationsAuthToken } from '@/config/env'
import { log } from '@/lib/logger'

export type ConversationStatus = 
  | 'active'
  | 'needs_review'
  | 'archived'
  | 'completed'
  | 'ai_responding' // Solo temporal, no se puede cambiar desde el select

/**
 * Basic shape of a Message as returned by the API
 */
export interface Message {
  id: string
  conversation: string
  text: string
  sender: string
  timestamp: string
  metadata?: Record<string, unknown>
}

/**
 * Service for interacting with the Conversations API
 */
export class ConversationService {
  private conversationsCache = new Map<string, { data: unknown, timestamp: number }>()
  private readonly serviceName = 'ConversationService'

  /**
   * Enhanced error handling with service-specific logging
   */
  private handleError(error: unknown, operation: string): never {
    const message = error instanceof ConversationsApiError 
      ? error.message 
      : `${this.serviceName} ${operation} failed: ${error}`
    
    // Don't log "Conversation not found" as an error since it's expected behavior
    const isConversationNotFound = message.includes('Conversation not found')
    
    if (isConversationNotFound) {
      log.debug(`${this.serviceName} ${operation}`, error, { 
        component: 'ConversationService', 
        function: 'handleError',
        service: this.serviceName,
        operation 
      })
    } else {
      log.error(`${this.serviceName} ${operation}`, error, { 
        component: 'ConversationService', 
        function: 'handleError',
        service: this.serviceName,
        operation 
      })
    }
    
    throw new Error(message)
  }

  /**
   * Log service operations for debugging
   */
  private log(operation: string, data?: unknown) {
    log.debug(`${this.serviceName} ${operation}`, data, {
      component: 'ConversationService',
      function: 'log',
      service: this.serviceName,
      operation
    })
  }

  /**
   * Validate required parameters
   */
  private validateRequired(params: Record<string, unknown>, required: string[]): void {
    for (const field of required) {
      if (!params[field]) {
        throw new Error(`Missing required parameter: ${field}`)
      }
    }
  }

  /**
   * Safe operation wrapper with error handling
   */
  private async safeOperation<T>(
    operation: string,
    fn: () => Promise<T>,
    errorMessage?: string
  ): Promise<T> {
    try {
      return await fn()
    } catch (error) {
      const message = errorMessage || `${operation} failed`
      this.handleError(error, message)
    }
  }

  /**
   * List conversations with optional pagination and status filter
   */
  /**
   * Sin cache a proposito: la lista se ordena por ultima actividad y cambia
   * con cada mensaje. Servir una pagina de hace dos minutos tras un refresco
   * de la primera perdia filas por el camino y dejaba el scroll pidiendo
   * paginas vacias para siempre (2026-09-14).
   */
  async listConversations(params?: {
    page?: number;
    pageSize?: number;
    /** Uno o varios separados por coma. */
    status?: ConversationStatus | string;
    source?: string;
    category?: string;
    tags?: string;
    tag_ids?: string;
    lead_source?: string;
    conversationId?: string;
    phone_number_id?: string;
    threads?: 'head' | 'all';
    search?: string;
    user_tags?: string;
  }): Promise<ConversationsListResponse> {
    return this.safeOperation('listConversations', async () => {
      const response = await conversationsApi.listConversations({
        page: params?.page,
        page_size: params?.pageSize,
        status: params?.status,
        source: params?.source,
        category: params?.category,
        tags: params?.tags,
        tag_ids: params?.tag_ids,
        lead_source: params?.lead_source,
        conversation_id: params?.conversationId,
        phone_number_id: params?.phone_number_id,
        threads: params?.threads,
        search: params?.search,
        user_tags: params?.user_tags,
      }) as ConversationsListResponse

      this.log('Conversations fetched', {
        count: response.results?.length || (response.conversations ? Object.values(response.conversations).flat().length : 0),
        params 
      })

      return response
    })
  }

  /**
   * Get a specific conversation by ID
   */
  async getConversation(id: string): Promise<Conversation> {
    this.validateRequired({ id }, ['id'])
    return this.safeOperation('getConversation', async () => {
      return await conversationsApi.getConversation(id) as Conversation
    })
  }

  /**
   * Create a new conversation
   */
  async createConversation(data: {
    source: string
    title?: string
    metadata?: Record<string, unknown>
    channel_user_id?: string
    channel_conversation_id?: string
  }): Promise<Conversation> {
    this.validateRequired({ source: data.source }, ['source'])
    return this.safeOperation('createConversation', async () => {
      // Use the conversations API create endpoint
      const response = await fetch(`${CONVERSATIONS_API_URL}/api/v1/conversations/create/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${getConversationsAuthToken()}`,
        },
        body: JSON.stringify(data),
      })

      if (!response.ok) {
        const errorText = await response.text()
        throw new Error(`Failed to create conversation: ${errorText}`)
      }

      const result = await response.json()
      // Handle both direct conversation and wrapped response
      return (result.conversation || result) as Conversation
    })
  }

  /**
   * Update conversation status
   */
  async updateStatus(id: string, status: ConversationStatus): Promise<Conversation> {
    this.validateRequired({ id, status }, ['id', 'status'])
    return this.safeOperation('updateStatus', async () => {
      // Clear cache for this conversation
      this.clearConversationCache(id)
      
      return await conversationsApi.updateConversationStatus(id, status) as Conversation
    })
  }

  /**
   * Mark a conversation as read
   */
  async markAsRead(id: string): Promise<void> {
    this.validateRequired({ id }, ['id'])
    return this.safeOperation('markAsRead', async () => {
      // Clear cache for this conversation
      this.clearConversationCache(id)
      
      await conversationsApi.markAsRead(id)
    })
  }

  /**
   * Archive a conversation
   */
  async archiveConversation(id: string): Promise<void> {
    this.validateRequired({ id }, ['id'])
    return this.safeOperation('archiveConversation', async () => {
      // Clear cache for this conversation
      this.clearConversationCache(id)
      
      await conversationsApi.archiveConversation(id)
    })
  }

  /**
   * Delete a conversation
   */
  async deleteConversation(id: string, deleteUser: boolean = false): Promise<void> {
    this.validateRequired({ id }, ['id'])
    return this.safeOperation('deleteConversation', async () => {
      // Clear cache for this conversation
      this.clearConversationCache(id)
      
      await conversationsApi.deleteConversation(id, deleteUser)
    })
  }

  /**
   * Get conversation messages
   */
  async getMessages(id: string, params?: { page?: number; pageSize?: number }): Promise<Message[]> {
    this.validateRequired({ id }, ['id'])
    return this.safeOperation('getMessages', async () => {
      // Convert pageSize to page_size for API
      const apiParams = params ? {
        page: params.page,
        page_size: params.pageSize
      } : undefined
      
      const response = await conversationsApi.getConversationMessages(id, apiParams) as { results: Message[] } | Message[]
      // Handle both paginated response format and legacy array format
      return Array.isArray(response) ? response : (response.results || [])
    })
  }

  /**
   * Send a message to a conversation
   */
  async sendMessage(id: string, text: string, sender: 'user' | 'admin' = 'user'): Promise<Message> {
    this.validateRequired({ id, text }, ['id', 'text'])
    return this.safeOperation('sendMessage', async () => {
      // Clear cache for this conversation
      this.clearConversationCache(id)
      
      return await conversationsApi.sendMessage(id, text, sender) as Message
    })
  }

  /**
   * Clear cache for a specific conversation
   */
  private clearConversationCache(conversationId: string): void {
    for (const [key] of this.conversationsCache) {
      if (key.includes(conversationId)) {
        this.conversationsCache.delete(key)
      }
    }
  }

  /**
   * Clear all cached data
   */
  clearCache(): void {
    this.conversationsCache.clear()
    this.log('Cache cleared')
  }

  /**
   * Get cache statistics
   */
  getCacheStats(): { size: number; keys: string[] } {
    return {
      size: this.conversationsCache.size,
      keys: Array.from(this.conversationsCache.keys())
    }
  }
}

// Export singleton instance
export const conversationService = new ConversationService()